import * as amplitude from "@amplitude/analytics-browser";
import { GeneralPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { WebhookStatus } from "@redotech/redo-merchant-general-app/setting/developer/types";
import { NotificationBannerItem } from "@redotech/redo-web/arbiter-components/notification/redo-notification-banner";
import { useEffect, useMemo } from "react";
import { PlatformEvents } from "../../../platform-events";
import { ActionRequiredSeverity } from "./action-required-severity";

export function useFixDeveloperWebhooks(): {
  severity: ActionRequiredSeverity;
  item: NotificationBannerItem;
} | null {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const team = useRequiredContext(TeamContext);

  const canManageTeamDevelopment = useHasPermission(
    GeneralPermissions.MANAGE_TEAM_DEVELOPMENT,
  );

  const webhooksLoad = useLoad(
    async (signal: AbortSignal) => {
      if (!canManageTeamDevelopment) {
        return { webhooks: [] };
      }

      return await rpcClient.getTeamWebhooks({}, { signal });
    },
    [rpcClient],
  );

  const hasDisabledWebhooks = useMemo(() => {
    if (webhooksLoad.pending || webhooksLoad.error || !webhooksLoad.value) {
      return false;
    }

    return webhooksLoad.value.webhooks.some(
      (webhook) => webhook.status === WebhookStatus.DISABLED,
    );
  }, [webhooksLoad]);

  useEffect(() => {
    if (hasDisabledWebhooks) {
      amplitude.track(PlatformEvents.DEVELOPER_FIX_WEBHOOKS_VIEWED);
    }
  }, [hasDisabledWebhooks]);

  if (!hasDisabledWebhooks) {
    return null;
  }

  return {
    severity: ActionRequiredSeverity.Low,
    item: {
      id: "fix-developer-webhooks",
      type: "warning",
      message:
        "Some of your webhook notifications have been disabled because of high failure rates. Please fix them in the developer settings.",
      actions: [
        {
          actionType: "link",
          href: `/stores/${team._id}/settings/developer`,
          text: "Fix webhooks",
          internal: true,
          onClick: () =>
            amplitude.track(PlatformEvents.DEVELOPER_FIX_WEBHOOKS_CLICKED),
        },
      ],
    },
  };
}
